import React, { useContext, useEffect, useRef, useState } from "react";
import useRoleListItems from "@/hooks/useRoleListItems";
import useVersion from "@/hooks/useVersion";
import MenuLink from "./MenuLink";
import useTheme from "@/hooks/useTheme";
import TodaysTipsModal from "@/components/TodaysTipsModal";
import { AdminTheme } from "@/context/ThemeContext";
import useApi from "@/hooks/useApi";
import TimezonePicker from "@/components/TimezonePicker";
import authService from "@/services/AuthService";
import {
  Avatar,
  LogOut01,
  OutlineBadge,
  Profile,
  StatusContext,
  User01
} from "@velaro/velaro-component-lib";
const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;

interface Props {
  profile: Profile;
  onClose(): void;
}

// ── Accent options (matches ThemePanel) ──────────────────────────────────────
interface AccentOption { name: string; base: string; color: string; }
const ACCENTS: AccentOption[] = [
  { name: "Blue",      base: "default",   color: "#1670e7" },
  { name: "Ocean",     base: "ocean",     color: "#3b5d88" },
  { name: "Navy",      base: "navy",      color: "#1a3a5c" },
  { name: "Cobalt",    base: "cobalt",    color: "#1a4480" },
  { name: "Teal",      base: "teal",      color: "#0d7373" },
  { name: "Turquoise", base: "turquoise", color: "#0e6b7a" },
  { name: "Forest",    base: "forest",    color: "#1b6d41" },
  { name: "Emerald",   base: "emerald",   color: "#166534" },
  { name: "Sage",      base: "sage",      color: "#3d6b55" },
  { name: "Seafoam",   base: "seafoam",   color: "#1a7b6b" },
  { name: "Slate",     base: "slate",     color: "#334155" },
  { name: "Grape",     base: "grape",     color: "#5f468c" },
  { name: "Rose",      base: "rose",      color: "#7c3155" },
];

function parseTheme(theme: AdminTheme): { base: string; dark: boolean } {
  if (theme === "light") return { base: "default", dark: false };
  if (theme === "dark")  return { base: "default", dark: true };
  if (theme.endsWith("-dark")) return { base: theme.replace(/-dark$/, ""), dark: true };
  return { base: theme, dark: false };
}
function buildTheme(base: string, dark: boolean): AdminTheme {
  if (base === "default") return dark ? "dark" : "light";
  return (dark ? `${base}-dark` : base) as AdminTheme;
}

// ── Quick light / dark toggle shown at the top of the menu ───────────────────
function LightDarkToggle() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark" || theme.endsWith("-dark");

  const toggle = () => {
    if (theme === "light") {
      setTheme("dark");
    } else if (theme === "dark") {
      setTheme("light");
    } else if (theme.endsWith("-dark")) {
      setTheme(theme.replace(/-dark$/, "") as Parameters<typeof setTheme>[0]);
    } else {
      setTheme(`${theme}-dark` as Parameters<typeof setTheme>[0]);
    }
  };

  return (
    <button
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="flex items-center w-full gap-3 px-1 py-2 rounded-lg hover:bg-slate-100 transition-colors text-sm text-slate-700"
    >
      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
        {isDark ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        )}
      </span>
      <span>{isDark ? "Light mode" : "Dark mode"}</span>
      <span className="ml-auto flex items-center">
        <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${isDark ? "bg-primary" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${isDark ? "translate-x-4" : "translate-x-0.5"}`} />
        </span>
      </span>
    </button>
  );
}

// ── Inline appearance panel (accent + preview) shown when expanded ────────────
function AppearancePanel() {
  const { theme, setTheme } = useTheme();
  const { base: currentBase, dark: currentDark } = parseTheme(theme);

  return (
    <div className="mt-1 rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-3">
      {/* Accent colors */}
      <div>
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Accent color</p>
        <div className="grid grid-cols-5 gap-2">
          {ACCENTS.map((accent) => {
            const active = currentBase === accent.base;
            return (
              <button
                key={accent.base}
                onClick={() => setTheme(buildTheme(accent.base, currentDark))}
                title={accent.name}
                aria-label={accent.name}
                aria-pressed={active}
                className="group flex flex-col items-center gap-1 focus:outline-none"
              >
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all shadow-sm ${
                    active ? "ring-2 ring-offset-1 ring-primary scale-110" : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: accent.color }}
                >
                  {active && (
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="white">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </span>
                <span className={`text-[9px] leading-none font-medium ${active ? "text-primary-active" : "text-slate-500"}`}>
                  {accent.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Preview strip */}
      <div className="rounded-lg overflow-hidden border border-slate-200 h-7 flex">
        <div className="w-8 flex-shrink-0" style={{ backgroundColor: ACCENTS.find(a => a.base === currentBase)?.color ?? "#1670e7" }} />
        <div className="flex-1 flex items-center px-3 gap-2" style={{ backgroundColor: currentDark ? "#1e293b" : "#f8fafc" }}>
          <div className="h-1.5 rounded-full flex-1" style={{ backgroundColor: currentDark ? "#334155" : "#e2e8f0" }} />
          <div className="h-1.5 w-5 rounded-full" style={{ backgroundColor: currentDark ? "#475569" : "#cbd5e1" }} />
        </div>
      </div>
    </div>
  );
}

function TimezonePanel() {
  const api = useApi();
  const [tz, setTz] = useState<string>(authService.profile?.timeZone ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  async function save(next: string) {
    setSaving(true);
    setError(null);
    try {
      const res = await api.admin.put("Profile/timezone", { timeZone: next || null });
      if (!res.ok) {
        setError(`Failed (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      setTz(data.timeZone ?? "");
      authService.updateProfileTimezone(data.timeZone ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-1 pb-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
        Override site timezone for your account
      </div>
      <TimezonePicker
        value={tz}
        disabled={saving}
        onChange={(e) => save(e.currentTarget.value)}
        className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
      >
      </TimezonePicker>
      <div className="flex items-center justify-between mt-1 text-[11px] text-slate-400">
        <span>Browser detected: {browserTz}</span>
        {tz && (
          <button onClick={() => save("")} disabled={saving}
            className="text-[var(--color-primary)] hover:underline">
            Use site default
          </button>
        )}
      </div>
      {error && <div className="text-[11px] text-red-500 mt-1">{error}</div>}
    </div>
  );
}

const STATUS_OPTIONS = [
  { label: "Available", color: "bg-green-500" },
  { label: "Away",      color: "bg-sweet-orange-500" },
  { label: "Offline",   color: "bg-slate-400" },
] as const;

export default function DesktopProfileMenu({ profile, onClose }: Props) {
  const { getRoleNameById } = useRoleListItems();
  const { version, deployedAt, messagingDeployedAt } = useVersion();

  // Deploy timestamps rendered in PT, only for @velaro.com users.
  const isVelaroStaff = profile.email?.toLowerCase().endsWith("@velaro.com") ?? false;
  const toPT = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: "America/Los_Angeles", timeZoneName: "short"
    });
  const formattedDeployTime = isVelaroStaff && deployedAt ? toPT(deployedAt) : null;
  const formattedMessagingDeployTime = isVelaroStaff && messagingDeployedAt ? toPT(messagingDeployedAt) : null;

  const menuRef = useRef<HTMLDivElement>(null);
  const [showTips, setShowTips] = useState(false);
  const [showAppearance, setShowAppearance] = useState(false);
  const [showTimezone, setShowTimezone] = useState(false);
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const { status, setStatus } = useContext(StatusContext);

  const statusStr = status as string;
  const currentStatusColor =
    statusStr === "Available" ? "bg-green-500" :
    statusStr === "Away" || statusStr === "Unavailable" ? "bg-sweet-orange-500" :
    "bg-slate-400";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.body.addEventListener("click", handleClickOutside);

    return () => {
      document.body.removeEventListener("click", handleClickOutside);
    };
  }, [onClose]);

  const renderRoleBadge = (role: string, index: number) => {
    let roleName = role;
    const parsedNumber = parseInt(role);
    if (!isNaN(parsedNumber)) {
      roleName = getRoleNameById(parsedNumber);
    }

    return <OutlineBadge key={index} label={roleName} size="sm" />;
  };

  const date = new Date();

  return (
    <div
      ref={menuRef}
      className="absolute bottom-0 left-full ml-2 z-20 text-slate-gray-600 shadow-lg border rounded-lg bg-white w-72 p-4"
    >
      <div className="flex items-center mb-4">
        <div className="v-avatar-sq flex-shrink-0">
          <Avatar
            name={
              profile.firstName && profile.lastName
                ? `${profile.firstName} ${profile.lastName}`
                : profile.displayName
            }
            url={profile.picture}
            size={48}
          />
        </div>
        <div className="pl-3 flex-grow min-w-0">
          <div className="font-semibold pl-1 break-words">
            {profile.firstName && profile.lastName
              ? `${profile.firstName} ${profile.lastName}`
              : profile.displayName}
          </div>
          <div className="flex flex-wrap items-center">
            {profile.roles?.map(renderRoleBadge)}
          </div>
        </div>
      </div>
      <hr className="border-slate-200 mb-2" />

      {/* Status selector */}
      <div className="relative mb-1">
        {showStatusPanel && (
          <div className="absolute bottom-full left-0 mb-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-30">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.label}
                onClick={() => { setStatus(opt.label as any); setShowStatusPanel(false); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-slate-50 text-sm text-slate-700"
              >
                <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${opt.color}`} style={{ borderRadius: 3 }} />
                {opt.label}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => setShowStatusPanel(v => !v)}
          className="flex items-center w-full gap-3 px-1 py-2 rounded-lg hover:bg-slate-100 transition-colors text-sm text-slate-700"
        >
          <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${currentStatusColor}`} style={{ borderRadius: 3 }} />
          <span className="flex-1 text-left">{status === "Unavailable" ? "Away" : status}</span>
          <svg className={`transition-transform ${showStatusPanel ? "rotate-180" : ""}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4.5L6 8l4-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <hr className="border-slate-200 mb-2" />
      <LightDarkToggle />

      {/* Appearance — accent color picker, expanded inline */}
      <button
        onClick={() => setShowAppearance((v) => !v)}
        className="flex items-center w-full gap-3 px-1 py-2 rounded-lg hover:bg-slate-100 transition-colors text-sm text-slate-700"
        aria-expanded={showAppearance}
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
          {/* Palette icon */}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>
            <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
            <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
            <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
          </svg>
        </span>
        <span>Appearance</span>
        <svg
          className={`ml-auto transition-transform ${showAppearance ? "rotate-180" : ""}`}
          width="12" height="12" viewBox="0 0 12 12" fill="none"
        >
          <path d="M2 4.5L6 8l4-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {showAppearance && <AppearancePanel />}

      {/* Time zone — per-user override of site timezone */}
      <button
        onClick={() => setShowTimezone((v) => !v)}
        className="flex items-center w-full gap-3 px-1 py-2 rounded-lg hover:bg-slate-100 transition-colors text-sm text-slate-700"
        aria-expanded={showTimezone}
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v6l4 2"/>
          </svg>
        </span>
        <span>Time zone</span>
        <span className="ml-auto text-xs text-slate-400 truncate max-w-[120px]">
          {authService.profile?.timeZone || "Site default"}
        </span>
        <svg
          className={`transition-transform ${showTimezone ? "rotate-180" : ""}`}
          width="12" height="12" viewBox="0 0 12 12" fill="none"
        >
          <path d="M2 4.5L6 8l4-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {showTimezone && <TimezonePanel />}

      <hr className="border-slate-200 mt-2" />

      {/* Today's inspiration — subtle daily tips entry point */}
      <button
        onClick={() => setShowTips(v => !v)}
        className="flex items-center w-full gap-3 px-1 py-2 rounded-lg hover:bg-slate-100 transition-colors text-sm text-slate-500"
        aria-label="View today's inspiration"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4l3 3"/>
          </svg>
        </span>
        <span>Today's inspiration</span>
        <svg className="ml-auto" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M4.5 2.5L7.5 6l-3 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {showTips && (
        <TodaysTipsModal onClose={() => setShowTips(false)} />
      )}

      <hr className="border-slate-200 mt-2" />
      <div className="whitespace-nowrap">
        <MenuLink
          to={`${messagingClientUrl}/profile`}
          onClose={onClose}
          icon={User01}
          label="Profile Details"
          className="flex items-center text-sm rounded-lg h-12 hover:bg-slate-gray-200 pr-4 py-2 mt-2"
          targetBlank={true}
        />
        <MenuLink
          to="/logout"
          onClose={onClose}
          icon={LogOut01}
          label="Log out"
          className="flex items-center text-sm rounded-lg h-12 hover:bg-slate-gray-200 pr-4 py-2"
        />
      </div>
      <hr className="border-slate-200 mb-3" />
      <div className="text-xs text-slate-400">
        <span>&copy; {date.getFullYear()} Velaro v{version}</span>
        {formattedDeployTime && (
          <div className="mt-0.5 text-slate-300">Admin deployed {formattedDeployTime}</div>
        )}
        {formattedMessagingDeployTime && (
          <div className="mt-0.5 text-slate-300">Messaging deployed {formattedMessagingDeployTime}</div>
        )}
      </div>
    </div>
  );
}
